import { createSignal } from "solid-js";

import { StorageBackendType } from "@uic/adapters/slorgproto";

const PROTO_PREFIX = "STORAGE_BACKEND_TYPE_";

const [backendType, setBackendTypeSignal] = createSignal(null);

let settling = Promise.resolve();
let releaseSettled = null;

/**
 * Closes the gate, so callers wait until a boot attempt settles. A gate already
 * closed stays as it is, keeping the promise its current waiters hold.
 */
function closeGate() {
  if (releaseSettled) return;

  settling = new Promise((resolve) => {
    releaseSettled = resolve;
  });
}

/**
 * Opens the gate, releasing everyone waiting at it.
 */
function openGate() {
  const release = releaseSettled;

  releaseSettled = null;
  if (release) release();
}

/**
 * Convert a proto JSON string (e.g. "STORAGE_BACKEND_TYPE_LOCALSTORAGE")
 * to its numeric enum value. tsEnum strips the prefix, so we must too.
 * @param {string} str The full proto enum string
 * @returns {number|undefined} The enum numeric value
 */
export function fromProtoString(str) {
  if (!str) return undefined;
  const short = str.startsWith(PROTO_PREFIX)
    ? str.slice(PROTO_PREFIX.length)
    : str;
  return StorageBackendType[short];
}

/**
 * Publishes the resolved storage backend, or `null` to return it to unknown.
 *
 * Returning it to unknown re-gates the wait, so nothing between here and the
 * next boot can read the departing session's backend as though it were its own.
 * Only a settled boot attempt opens the gate again, because a localStorage
 * backend is not usable until `bootBackend` has seeded the browser mirror.
 * @param {number|null} type The storage backend type as its numeric enum value
 */
export function setBackendType(type) {
  setBackendTypeSignal(type);

  if (type === null) closeGate();
}

/**
 * @returns {boolean} Whether the active backend is localStorage
 */
export function isLocalStorage() {
  return backendType() === StorageBackendType.LOCALSTORAGE;
}

/**
 * @returns {boolean} Whether the backend has resolved to a usable type
 */
export function isBackendReady() {
  return (
    backendType() !== null && backendType() !== StorageBackendType.UNSPECIFIED
  );
}

/**
 * Records the backend resolution currently in flight so callers that must not
 * guess the backend can wait for it.
 *
 * Each boot re-gates the wait, so a second sign-in cannot be released by the
 * resolution of the session before it.
 * @param {Promise<unknown>} promise The resolution in flight
 * @returns {Promise<void>} Resolves once the attempt has settled and the gate is open
 */
export function trackBackendBoot(promise) {
  closeGate();

  return Promise.resolve(promise)
    .catch(() => {})
    .then(openGate);
}

/**
 * Waits for the backend resolution to finish, whether it produced a usable type
 * or failed. A failed boot resolves too, so its callers proceed and surface
 * their own errors rather than hanging on a backend that will never arrive.
 * @returns {Promise<void>} Resolves once the boot attempt has settled
 */
export function whenBackendSettled() {
  return settling;
}
